import React, {useState, useRef} from 'react';
import PropTypes from 'prop-types';
import {useSelector, useDispatch} from 'react-redux';
import {RadioGroup, Text, Box, Alert} from '@spothero/ui';
import {scrollToRef} from 'utils/dom-utils';
import {Controller} from 'react-hook-form';
import VehicleDisplay from '../vehicle-display';
import AddVehicle from '../add-vehicle';
import {updateVehicle} from 'store/parking-pass/parking-pass-actions';
import BorderedRadio from 'common/bordered-radio';
import {PROPTYPE_RESERVATION} from '@/proptypes/index';
import VehicleAPI from 'api/vehicle';
import ReservationUtils from 'utils/reservation-utils';
import ReservationAPI from 'api/reservation';
import {PROPTYPE_RESERVATION_VEHICLE} from '@/proptypes/reservation-prop-types';
import {addVehicle} from 'store/user/user-actions';
import UserUtils from 'utils/user-utils';

export const VEHICLE_FORM_ID = 'vehicle-form';
export const ADD_NEW_VEHICLE_VALUE = 'addNewVehicle';
export const VEHICLE_FIELD = 'vehicle';

const VehicleSelect = ({
    reservation,
    vehicles,
    handleSubmit,
    handleSuccess,
    handleRateDiff,
    control,
    setError,
    error,
}) => {
    const dispatch = useDispatch();
    const errorMessageRef = useRef(null);

    const {
        data: {id: userId, status: userStatus},
    } = useSelector(state => state.user);
    const isGuestUser = userStatus !== UserUtils.AUTH_STATE.USER;
    const {accessKey} = useSelector(state => state.parkingPass);

    const [vehicleToAdd, setVehicleToAdd] = useState({
        vehicleInfoId: null,
        licensePlateValue: null,
        licensePlateState: null,
        isDefault: false,
        unlistedModel: false,
        makeLabel: null,
        modelLabel: null,
    });

    const handleNewVehicleChange = vehicleChanges => {
        setVehicleToAdd(prevState => {
            return {...prevState, ...vehicleChanges};
        });
    };

    const [newVehicleErrors, setNewVehicleErrors] = useState([]);

    const storeNewVehicle = async () => {
        // if there were previous add new vehicle errors, clear them
        if (newVehicleErrors.length) {
            setNewVehicleErrors([]);
        }
        /* eslint-disable camelcase */
        const vehicleToSubmit = {
            vehicle_info_id: vehicleToAdd.vehicleInfoId,
            license_plate: vehicleToAdd.licensePlateValue,
            license_plate_state: vehicleToAdd.licensePlateState,
            is_default: vehicleToAdd.isDefault,
        };

        if (vehicleToAdd.unlistedModel) {
            // if it's an unlisted vehicle don't submit a vehicle_info_id
            delete vehicleToSubmit.vehicle_info_id;
            vehicleToSubmit.unlisted_model = vehicleToAdd.unlistedModel;
        }
        /* eslint-enable camelcase */

        const {
            data: {data},
        } = await VehicleAPI.add(vehicleToSubmit, userId);

        return {
            vehicleProfileId: data.id,
            isDefault: data.is_default,
            licensePlateState: data.license_plate.state,
            licensePlateValue: data.license_plate.value,
            unlistedModel: data.unlisted_model,
            makeLabel: data.info?.display_make,
            modelLabel: data.info?.display_model,
        };
    };

    const generalHTTPError = () => {
        // While we could use our own piece of state rather than intertwining this with the form errors,
        // we get some things for free like clearing the error onChange
        setError(VEHICLE_FIELD, {
            type: 'custom',
            message:
                'Oops, we hit a snag saving your vehicle. Please try again.',
        });
        scrollToRef(errorMessageRef);
    };

    const onSubmit = async ({vehicle: vehicleValue}) => {
        let addedVehicle = {};
        let response = {};

        // check if add new vehicle was selected
        if (vehicleValue === ADD_NEW_VEHICLE_VALUE) {
            // new vehicle must either have vehicle_info_id or be unlisted
            if (!vehicleToAdd.vehicleInfoId && !vehicleToAdd.unlistedModel) {
                if (
                    !newVehicleErrors.includes(
                        ReservationUtils.NEW_VEHICLE_ERRORS.NO_VEHICLE
                    )
                ) {
                    setNewVehicleErrors(prevState => {
                        return [
                            ...prevState,
                            ReservationUtils.NEW_VEHICLE_ERRORS.NO_VEHICLE,
                        ];
                    });
                }

                return null;
            }

            try {
                if (isGuestUser) {
                    addedVehicle = vehicleToAdd;
                } else {
                    addedVehicle = await storeNewVehicle();
                }
            } catch {
                generalHTTPError();

                return;
            }
        }

        let rateDiff = {};
        const reservationVehicle =
            vehicleValue === ADD_NEW_VEHICLE_VALUE
                ? addedVehicle
                : vehicles.find(
                      vehicle => vehicle.vehicleProfileId === vehicleValue
                  );
        const skipRateDiffCheck =
            reservationVehicle.unlistedModel ||
            reservation.reservationType ===
                ReservationUtils.RESERVATION_TYPES.MONTHLY;

        if (!skipRateDiffCheck) {
            try {
                rateDiff = await ReservationAPI.getVehicleRateDifference({
                    reservationId: reservation.rentalId,
                    // If logged in user will have vehicleProfileId, if guest vehicleInfoId
                    vehicleProfileId: reservationVehicle.vehicleProfileId,
                    vehicleInfoId: reservationVehicle.vehicleInfoId,
                    accessKey,
                });
            } catch {
                if (addedVehicle.vehicleProfileId) {
                    dispatch(addVehicle(addedVehicle));
                }
                generalHTTPError();

                return;
            }
        }

        if (
            !skipRateDiffCheck &&
            rateDiff.originalRental.price !== rateDiff.priceBreakdown?.price
        ) {
            handleRateDiff({
                vehicle: reservationVehicle,
                rateDiff,
            });
        } else {
            response = await dispatch(
                updateVehicle({
                    vehicleProfileId: reservationVehicle.vehicleProfileId,
                    reservationId: reservation.rentalId,
                    price: reservation.priceBreakdown.totalPrice,
                    accessKey,
                    guestVehicle: reservationVehicle,
                })
            );

            if (response.success) {
                handleSuccess();
            } else {
                generalHTTPError();
            }
        }

        if (addedVehicle.vehicleProfileId) {
            dispatch(addVehicle(addedVehicle));
        }
    };

    return (
        <Box
            as="form"
            onSubmit={handleSubmit(onSubmit)}
            id={VEHICLE_FORM_ID}
            width="100%"
            sx={{'.chakra-form-control': {paddingX: 0}}}
        >
            {vehicles.length === 0 ? (
                <Box marginBottom={4}>
                    <AddVehicle
                        onChange={handleNewVehicleChange}
                        errors={newVehicleErrors}
                        hideDefaultCheckbox={isGuestUser}
                    />
                </Box>
            ) : (
                <Controller
                    name={VEHICLE_FIELD}
                    control={control}
                    render={({field}) => (
                        <RadioGroup
                            sx={{
                                '.chakra-radio__control': {
                                    marginTop: 0.5,
                                },
                                '.chakra-radio__label': {
                                    overflow: 'hidden',
                                },
                            }}
                            marginBottom={4}
                            id={VEHICLE_FIELD}
                            label="Vehicle"
                            {...field}
                        >
                            {vehicles.map(vehicle => (
                                <BorderedRadio
                                    key={vehicle.vehicleProfileId}
                                    isSelected={
                                        field.value === vehicle.vehicleProfileId
                                    }
                                    value={vehicle.vehicleProfileId}
                                >
                                    <VehicleDisplay
                                        make={vehicle.makeLabel}
                                        model={vehicle.modelLabel}
                                        plate={vehicle.licensePlateValue}
                                    />
                                </BorderedRadio>
                            ))}
                            <Box
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor={
                                    field.value === ADD_NEW_VEHICLE_VALUE
                                        ? 'primary.default'
                                        : 'gray.200'
                                }
                            >
                                <BorderedRadio
                                    isSelected={
                                        field.value === ADD_NEW_VEHICLE_VALUE
                                    }
                                    isParent
                                    value={ADD_NEW_VEHICLE_VALUE}
                                >
                                    <Text data-testid="VehicleSelect-radio-add-new-vehicle">
                                        Add New Vehicle
                                    </Text>
                                </BorderedRadio>
                                {field.value === ADD_NEW_VEHICLE_VALUE && (
                                    <Box
                                        padding={4}
                                        borderTopWidth="1px"
                                        borderTopColor="gray.200"
                                    >
                                        <AddVehicle
                                            onChange={handleNewVehicleChange}
                                            errors={newVehicleErrors}
                                            hideDefaultCheckbox={isGuestUser}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </RadioGroup>
                    )}
                />
            )}
            {error && (
                <Alert
                    ref={errorMessageRef}
                    title={error.message}
                    showIcon
                    status="error"
                    size="md"
                />
            )}
        </Box>
    );
};

VehicleSelect.propTypes = {
    reservation: PROPTYPE_RESERVATION,
    error: PropTypes.shape({
        message: PropTypes.string.isRequired,
    }),
    vehicles: PropTypes.arrayOf(PROPTYPE_RESERVATION_VEHICLE).isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleRateDiff: PropTypes.func.isRequired,
    handleSuccess: PropTypes.func.isRequired,
    control: PropTypes.object.isRequired,
    setError: PropTypes.func.isRequired,
};

export default VehicleSelect;
